import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ScrollText } from 'lucide-react';
import { Panel, Field, FormRow, ErrorState } from './design';

// The provider's own rules that change what is charged: the notice that makes a cancellation free
// (the agreement Rostery prints states the same figure), and the care management price per hour with
// any extra work the provider does not claim as care management.
const toForm = (d) => ({ sah_notice_days: d?.sah_notice_days ?? '', chsp_notice_days: d?.chsp_notice_days ?? '',
  cm_hourly_price: d?.cm_hourly_price ?? '', cm_excluded: (d?.cm_excluded || []).join('\n'), price_notice_days: d?.price_notice_days ?? '', require_note_at_clockout: !!d?.require_note_at_clockout,
  complaint_ack_days: d?.complaint_ack_days ?? 5, complaint_resolve_days: d?.complaint_resolve_days ?? 30 });

export default function ProviderRules() {
  const [f, setF] = useState({ sah_notice_days: '', chsp_notice_days: '', cm_hourly_price: '', cm_excluded: '', price_notice_days: '', require_note_at_clockout: false, complaint_ack_days: 5, complaint_resolve_days: 30 });
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  // Without the saved settings the form has nothing to compare against, so it could never offer
  // Save; a failed load says so, with a retry, instead of showing a form that cannot be saved.
  const [loadError, setLoadError] = useState(null);
  const load = useCallback(() => {
    setLoadError(null);
    api.get('/aged-care/program-settings')
      .then((r) => { const v = toForm(r.data); setF(v); setSaved(v); })
      .catch((e) => setLoadError(e.response?.data?.message || 'Could not load your service agreement rules'));
  }, []);
  useEffect(() => { load(); }, [load]);
  const dirty = saved && Object.keys(f).some((k) => String(f[k]) !== String(saved[k]));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/aged-care/program-settings', { ...f, cm_excluded: String(f.cm_excluded || '').split('\n') });
      const v = toForm(r.data);
      setF(v); setSaved(v); toast.success('Rules saved');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the rules'); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  if (loadError) {
    return (
      <Panel title="Your service agreement rules" icon={ScrollText} className="mb-5">
        <ErrorState message={loadError} onRetry={load} />
      </Panel>
    );
  }
  if (!saved) {
    return (
      <Panel title="Your service agreement rules" icon={ScrollText} className="mb-5" bodyClass="px-4 py-3">
        <div className="skeleton h-40 rounded-lg" aria-busy="true" aria-label="Loading" />
      </Panel>
    );
  }
  return (
    <Panel title="Your service agreement rules" icon={ScrollText} className="mb-5" bodyClass="px-4 py-3"
      actions={dirty ? <button type="button" onClick={save} disabled={busy} className="btn-primary btn-sm">{busy ? 'Saving' : 'Save'}</button> : null}>
      <FormRow cols={2}>
        <Field label="Support at Home: notice for a free cancellation" hint="Business days. With this much notice a cancelled visit is not charged. Blank uses 2, and the service agreement states it.">
          {(id) => <input id={id} type="number" min="0" max="14" className="input-field" value={f.sah_notice_days} onChange={set('sah_notice_days')} placeholder="2" />}
        </Field>
        <Field label="CHSP: notice for a free cancellation" hint="Business days. Leave blank to let your cancellation policy decide on its own.">
          {(id) => <input id={id} type="number" min="0" max="14" className="input-field" value={f.chsp_notice_days} onChange={set('chsp_notice_days')} placeholder="Policy decides" />}
        </Field>
      </FormRow>
      <FormRow cols={2}>
        <Field label="Care management price per hour ($)" hint="Approved care management time is claimed from your pooled account at this price. Part hours are claimed.">
          {(id) => <input id={id} type="number" min="0" step="0.01" className="input-field" value={f.cm_hourly_price} onChange={set('cm_hourly_price')} placeholder="Not set" />}
        </Field>
        <Field label="Other work you do not claim as care management" hint="One per line. Rostering, invoicing, travel time and general administration are never claimed.">
          {(id) => <textarea id={id} rows={3} className="input-field" value={f.cm_excluded} onChange={set('cm_excluded')} placeholder="For example: Team meeting" />}
        </Field>
      </FormRow>
      <FormRow cols={2}>
        <Field label="Notice before a price increase" hint="Days. A price increase cannot apply sooner, and each participant gets a new agreement version to agree. Blank uses 28.">
          {(id) => <input id={id} type="number" min="0" max="180" className="input-field" value={f.price_notice_days} onChange={set('price_notice_days')} placeholder="28" />}
        </Field>
        <Field label="Acknowledge a complaint within (days)" hint="Your complaints policy. Overdue ones are on the overview.">
          {(id) => <input id={id} type="number" min="1" max="30" className="input-field" value={f.complaint_ack_days} onChange={set('complaint_ack_days')} />}
        </Field>
        <Field label="Resolve a complaint within (days)">
          {(id) => <input id={id} type="number" min="1" max="180" className="input-field" value={f.complaint_resolve_days} onChange={set('complaint_resolve_days')} />}
        </Field>
        <Field label="Notes" hint="Workers' notes are read for a change in needs, and go to a care partner to review (Care plans).">
          {() => (
            <label className="flex items-center gap-2 text-[13px] text-slate-700 py-2 cursor-pointer">
              <input type="checkbox" checked={!!f.require_note_at_clockout} onChange={(e) => setF((p) => ({ ...p, require_note_at_clockout: e.target.checked }))} /> A note is required to clock out of an aged care visit
            </label>
          )}
        </Field>
      </FormRow>
      <p className="text-[12px] text-slate-500 mt-2">
        Support at Home prices cover the whole cost of each service: package management, administration, travel, entry and exit fees are refused wherever a price or a line is entered.
      </p>
    </Panel>
  );
}
